/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: 'B2C_1_SIGNUP_SIGNIN',
    forgotPassword: 'B2C_1_PASSWORDRESET',
    editProfile: 'B2C_1_PROFILEEDIT',
  },
  authorities: {
    signUpSignIn: {
      authority: 'https://physioextraadb2c.b2clogin.com/physioextraadb2c.onmicrosoft.com/B2C_1_SIGNUP_SIGNIN',
    },
    forgotPassword: {
      authority: 'https://physioextraadb2c.b2clogin.com/physioextraadb2c.onmicrosoft.com/B2C_1_PASSWORDRESET',
    },
    editProfile: {
      authority: 'https://physioextraadb2c.b2clogin.com/physioextraadb2c.onmicrosoft.com/B2C_1_PROFILEEDIT',
    },
  },
  authorityDomain: 'physioextraadb2c.b2clogin.com',
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: 'f8fb7be4-47ee-4ed8-83e6-ab7a7445f518', // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Use a sign-up
    // /sign-in user-flow as a default authority
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: window.location.origin, // Points to
    // window.location.origin. You must register this URI on Azure Portal/App Registration.
    postLogoutRedirectUri: window.location.origin, // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate
    // back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level, message, containsPii,
      ) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            // eslint-disable-next-line no-console
            console.error(message);
            return;
          case LogLevel.Info:
            if (process.env.DEBUG_MSAL) {
              // eslint-disable-next-line no-console
              console.info(message);
            }
            return;
          case LogLevel.Verbose:
            if (process.env.DEBUG_MSAL) {
              // eslint-disable-next-line no-console
              console.debug(message);
            }
            return;
          case LogLevel.Warning:
            if (process.env.DEBUG_MSAL) {
              // eslint-disable-next-line no-console
              console.warn(message);
            }
            return;
          default:
            if (process.env.DEBUG_MSAL) {
              // eslint-disable-next-line no-console
              console.debug(message);
            }
        }
      },
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = (locale) => ({
  scopes: [],
  extraQueryParameters: { ui_locales: locale },
});

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
  scopes: ['openid', 'profile'],
  loginHint: 'example@domain.net',
};
